/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * ConditionRuleDialog — configures a value that depends on a condition.
 *
 * WHY A DIALOG. The button card already carries a form, a table, a row per
 * column and a dedupe key, all in the narrow half of a split pane. A condition
 * is a source, an operator, a value and a result — per branch — and inline it
 * turned the card into a ladder nobody could read. Configured or not, the card
 * now grows by ONE summary line, exactly as SuccessRevealDialog does for the
 * pass.
 *
 * WHAT IT WRITES. The stored rule is
 *
 *   { from: 'when', test: <rule>, cases: [{op, value, then: <rule>}], else: <rule> }
 *
 * `test` reuses the value sources a column mapping already offers, so a
 * condition can read this record or another table. Results are fixed values
 * here: every case discussed writes a status word, and the server accepts any
 * rule shape, so a lookup-as-result stays configurable without a control nobody
 * asked for.
 *
 * `else` is REQUIRED and the dialog will not save without it. A rule that
 * matched nothing would write null into a column the builder believed they had
 * filled, which is not diagnosable from the row afterwards.
 *
 * Edits a DRAFT. Nothing reaches the stored configuration until Save, so Cancel
 * and Escape genuinely discard.
 */

import React, { useEffect, useRef, useState } from 'react';

import { useDialogInset } from './dialogInset';
import Hint from './Hint';
import LookupFields from './LookupFields';
import { CONDITION_OPS, OPS_WITH_VALUE, conditionValue } from './conditionOps';

/* What the condition may look at. Deliberately the two sources that answer the
 * question "something about this record" — the record itself, or a row it
 * points at. The other sources a column can use (a form answer, a ref) either
 * come from the visitor or do not exist yet when the rule runs. */
const TEST_SOURCES = [
  { key: 'record_field', label: 'A field on this record' },
  { key: 'lookup',       label: 'A value from another table' },
];

const EMPTY_LOOKUP = { table: '', matchColumn: '', matchSource: 'record_id',
                       matchValue: '', field: '' };

function lookupRuleToDraft(rule) {
  const matchTo = rule.match_to || {};
  return {
    table: rule.table || '',
    matchColumn: rule.match_column || '',
    matchSource: 'const' in matchTo ? 'const' : (matchTo.from || 'record_id'),
    matchValue: 'const' in matchTo ? matchTo.const : (matchTo.field || ''),
    field: rule.field || '',
  };
}

function draftToLookupRule(l) {
  const matchTo = l.matchSource === 'const'
    ? { const: l.matchValue }
    : l.matchSource === 'record_field'
      ? { from: 'record_field', field: l.matchValue }
      : { from: l.matchSource };
  return { from: 'lookup', table: l.table, match_column: l.matchColumn,
           match_to: matchTo, field: l.field };
}

/** A stored result rule -> the text in its box. Only fixed values are edited here. */
function resultText(rule) {
  if (rule && typeof rule === 'object' && 'const' in rule) return String(rule.const ?? '');
  return '';
}

function draftFrom(rule) {
  const r = rule || {};
  const test = r.test || {};
  const isLookup = test.from === 'lookup';
  return {
    testSource: isLookup ? 'lookup' : 'record_field',
    testField: test.from === 'record_field' ? (test.field || '') : '',
    lookup: isLookup ? lookupRuleToDraft(test) : { ...EMPTY_LOOKUP },
    cases: (r.cases || []).map(c => ({
      op: c.op || 'eq',
      value: Array.isArray(c.value) ? c.value.join(', ') : (c.value ?? ''),
      then: resultText(c.then),
    })),
    otherwise: resultText(r.else),
  };
}

export default function ConditionRuleDialog({
  column, rule, tables, columnsOf, recordColumns, matchSources, onSave, onCancel,
}) {
  const [draft, setDraft] = useState(() => draftFrom(rule));
  const dialogRef = useRef(null);
  const inset = useDialogInset();

  useEffect(() => {
    if (dialogRef.current) dialogRef.current.focus();
    function onKeyDown(e) { if (e.key === 'Escape') onCancel(); }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onCancel]);

  const set = changes => setDraft(d => ({ ...d, ...changes }));

  function setCase(position, changes) {
    setDraft(d => ({
      ...d,
      cases: d.cases.map((c, i) => (i === position ? { ...c, ...changes } : c)),
    }));
  }

  function addCase() {
    setDraft(d => ({ ...d, cases: [...d.cases, { op: 'eq', value: '', then: '' }] }));
  }

  function removeCase(position) {
    setDraft(d => ({ ...d, cases: d.cases.filter((_, i) => i !== position) }));
  }

  /* What stops a Save. Each is something the server would refuse or write
   * wrongly, said here while it can still be corrected. */
  const problems = [];
  if (draft.testSource === 'record_field' && !draft.testField) {
    problems.push('Choose the field the condition looks at.');
  }
  if (draft.testSource === 'lookup'
      && !(draft.lookup.table && draft.lookup.matchColumn && draft.lookup.field)) {
    problems.push('Finish the lookup: a table, a column to match on, and a column to read.');
  }
  if (!draft.cases.length) {
    problems.push('Add at least one condition.');
  }
  draft.cases.forEach((c, i) => {
    if (OPS_WITH_VALUE.includes(c.op) && String(c.value).trim() === '') {
      problems.push(`Condition ${i + 1} needs a value to compare against.`);
    }
    if (String(c.then).trim() === '') {
      problems.push(`Condition ${i + 1} needs a value to write.`);
    }
  });
  if (String(draft.otherwise).trim() === '') {
    problems.push('Say what to write when no condition matches.');
  }

  function save() {
    const test = draft.testSource === 'lookup'
      ? draftToLookupRule(draft.lookup)
      : { from: 'record_field', field: draft.testField };

    onSave({
      from: 'when',
      test,
      cases: draft.cases.map(c => ({
        op: c.op,
        ...(OPS_WITH_VALUE.includes(c.op) ? { value: conditionValue(c.op, c.value) } : {}),
        then: { const: c.then },
      })),
      else: { const: draft.otherwise },
    });
  }

  /* The sentence under the branches. The mapping is the part that needs
   * explaining — the widgets say what they are. */
  const looksAt = draft.testSource === 'lookup'
    ? (draft.lookup.field
        ? `${draft.lookup.field} on the matching ${draft.lookup.table || '…'} row`
        : 'a value from another table')
    : (draft.testField || 'a field on this record');

  return (
    <div
      className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center
                 justify-content-center"
      style={{ background: 'rgba(24,22,44,.42)', zIndex: 1080, overflowY: 'auto',
               // Padding is set here rather than with Bootstrap's `p-3`, whose
               // `!important` beats an inline paddingLeft — which is how the
               // inset below silently did nothing the first time.
               padding: '1rem',
               // Centre on the work area, not the window. See dialogInset.
               paddingLeft: `calc(1rem + ${inset}px)` }}
      onMouseDown={e => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div
        className="bg-white rounded-3 shadow-lg d-flex flex-column"
        role="dialog"
        aria-modal="true"
        aria-label={`Set ${column} by condition`}
        tabIndex={-1}
        ref={dialogRef}
        style={{ maxWidth: 620, width: '100%', maxHeight: '86vh' }}
      >
        <div className="px-3 py-3 border-bottom d-flex justify-content-between align-items-start">
          <div>
            <div className="fw-bold">
              Set <code>{column}</code> by condition
            </div>
            <div className="small text-muted">
              The server reads the value it tests from the database, so the visitor
              cannot change which branch is taken.
            </div>
          </div>
          <button type="button" className="btn-close" aria-label="Close" onClick={onCancel} />
        </div>

        <div className="px-3 py-3 overflow-auto">

          <div className="text-uppercase small fw-semibold text-muted border-bottom pb-1 mb-2">
            What to look at
          </div>

          <div className="mb-2">
            <label className="form-label small fw-semibold mb-1" htmlFor="cond-source">
              Source
            </label>
            <select
              id="cond-source" className="form-select form-select-sm"
              value={draft.testSource}
              onChange={e => set({ testSource: e.target.value })}
            >
              {TEST_SOURCES.map(s => (
                <option key={s.key} value={s.key}>{s.label}</option>
              ))}
            </select>
          </div>

          {draft.testSource === 'record_field' && (
            <div className="mb-3">
              <label className="form-label small fw-semibold mb-1" htmlFor="cond-field">
                Which field
              </label>
              <select
                id="cond-field" className="form-select form-select-sm"
                value={draft.testField}
                onChange={e => set({ testField: e.target.value })}
              >
                <option value="">— choose a column —</option>
                {(recordColumns || []).map(c => (
                  <option key={c.name} value={c.name}>{c.name}</option>
                ))}
              </select>
            </div>
          )}

          {draft.testSource === 'lookup' && (
            <div className="mb-3">
              <LookupFields
                column={'the condition'}
                value={draft.lookup}
                tables={tables}
                columnsOf={columnsOf}
                matchSources={matchSources}
                onChange={changes => set({ lookup: { ...draft.lookup, ...changes } })}
              />
            </div>
          )}

          <div className="text-uppercase small fw-semibold text-muted border-bottom pb-1 mb-2">
            Then decide
          </div>

          {draft.cases.map((c, i) => (
            <div key={i} className="p-2 mb-2 rounded border bg-body-tertiary">
              <div className="d-flex align-items-center gap-2 mb-1">
                <span className="small text-muted text-end"
                      style={{ minWidth: 62, flexShrink: 0 }}>
                  {i === 0 ? 'If it' : 'Or if it'}
                </span>
                <select
                  className="form-select form-select-sm"
                  aria-label={`Operator for condition ${i + 1}`}
                  value={c.op}
                  onChange={e => setCase(i, { op: e.target.value })}
                >
                  {CONDITION_OPS.map(o => (
                    <option key={o.key} value={o.key}>{o.label}</option>
                  ))}
                </select>
                {OPS_WITH_VALUE.includes(c.op) && (
                  <input
                    className="form-control form-control-sm"
                    aria-label={`Compare value for condition ${i + 1}`}
                    placeholder={c.op === 'in' ? 'one, two, three' : 'value'}
                    value={c.value}
                    onChange={e => setCase(i, { value: e.target.value })}
                  />
                )}
                <button
                  type="button" className="btn btn-sm btn-link text-danger p-0 px-1"
                  aria-label={`Remove condition ${i + 1}`}
                  onClick={() => removeCase(i)}
                >
                  &times;
                </button>
              </div>
              <div className="d-flex align-items-center gap-2">
                <span className="small text-muted text-end"
                      style={{ minWidth: 62, flexShrink: 0 }}>
                  write
                </span>
                <input
                  className="form-control form-control-sm"
                  aria-label={`Value to write for condition ${i + 1}`}
                  placeholder={`what goes in ${column}`}
                  value={c.then}
                  onChange={e => setCase(i, { then: e.target.value })}
                />
              </div>
            </div>
          ))}

          <button
            type="button" className="btn btn-sm btn-outline-secondary mb-3"
            onClick={addCase}
          >
            Add a condition
          </button>

          <div className="d-flex align-items-center gap-2 mb-2">
            <span className="small text-muted text-end"
                  style={{ minWidth: 62, flexShrink: 0 }}>
              Otherwise
            </span>
            <input
              className="form-control form-control-sm"
              aria-label="Value to write when no condition matches"
              placeholder={`what goes in ${column}`}
              value={draft.otherwise}
              onChange={e => set({ otherwise: e.target.value })}
            />
          </div>

          <Hint>
            {`Reads ${looksAt} on the server. `}
            {draft.cases.length
              ? draft.cases
                  .filter(c => String(c.then).trim() !== '')
                  .map(c => {
                    const op = (CONDITION_OPS.find(o => o.key === c.op) || {}).label || c.op;
                    const val = OPS_WITH_VALUE.includes(c.op) ? ` ${c.value}` : '';
                    return `If it ${op}${val}, writes ${c.then}. `;
                  })
                  .join('')
              : ''}
            {String(draft.otherwise).trim() !== ''
              ? `Otherwise writes ${draft.otherwise}.`
              : ''}
          </Hint>

          {problems.length > 0 && (
            <Hint tone="warn">
              {problems[0]}
            </Hint>
          )}
        </div>

        <div className="px-3 py-2 border-top d-flex justify-content-end gap-2">
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button" className="btn btn-sm btn-primary"
            disabled={problems.length > 0}
            onClick={save}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}
